import React, { useCallback, useContext } from 'react';

import CardSection from '../../common/CardSection';

import './Staffing.scss';
import User from '../../../models/User';
import PagedResultSet from '../../../models/common/PagedResultSet';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import AddAdminStaffDialog from 'src/components/admin/AddStaffDialog';
import ProjectAdministrationType from 'src/models/ProjectAdministrationType';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import { ODataOptions } from 'src/util/ODataBuilder';
import ProjectStaff from 'src/models/ProjectStaff';
import { useEffect, useState } from 'react';
import Landman from 'src/models/Landman';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Filter from 'src/components/common/Filter';
import CardContainer from 'src/components/common/CardContainer.redesign';
import * as _ from 'lodash';
import Card from 'src/components/common/Card.redesign';
import moment from 'moment';
import StaffingLandmanCard from './StaffingLandmanCard';
import useCollapse from 'react-collapsed';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';

import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { useDispatch } from 'react-redux';
import StaffedLandmanCard from './StaffedLandmanCard';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import ProjectBilling from 'src/models/projectSettings/ProjectBilling';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';
import ProjectDetails from 'src/models/projectSettings/ProjectDetails';

interface IStaffingPageProps {
    hideDialog(arg0: boolean): unknown;
    clientNumber?: string;
    projectNumber?: string;
}

const StaffingPage = (props: IStaffingPageProps) => {
    const dispatch = useDispatch();

    // TODO: Set up appContext.
    const { handleSubmit } = useForm();
    const { get, post, response } = useFetch();
    const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
    const appContext = useContext<IAppContextState>(AppContext);

    // Initial Data for Staffing Page

    const [projectStaff, setProjectStaff] = useState<PagedResultSet<ProjectStaff>>(undefined);
    const [projectAdministrationTypes, setProjectAdministrationTypes] = useState<Array<ProjectAdministrationType>>([]);
    const [projectBilling, setProjectBilling] = useState<ProjectBilling>(undefined);
    const [projectDetails, setProjectDetails] = useState<ProjectDetails>(undefined);
    const [users, setUsers] = useState<PagedResultSet<User>>(undefined);
    const [selectedLandmen, setSelectedLandmen] = useState<Landman[]>([]);
    const [staffedLandmen, setStaffedLandmen] = useState<Landman[]>([]);
    const [projectAdminStaff, setProjectAdminStaff] = useState<PagedResultSet<ProjectAdministratorStaff>>(undefined);

    // New States for Landman List
    const [landmen, setLandmen] = useState<Landman[]>([]);
    const [pcapmList, setPcapmList] = useState<User[]>([]);
    const [landmenFilters, setLandmenFilters] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [changesSubmitted, setChangesSubmitted] = useState<boolean>(false);
    const [staffComplete, setStaffComplete] = useState<boolean>(false);
    const [billingCycles, setBillingCycles] = useState<BillingAndPaymentCycle[]>([]);
    const [workClassifications, setWorkClassifications] = useState<ProjectWorkClassification[]>([]);

    useEffect(() => {
        getPCAPMUsers();
        getBillingCycles();
        getProjAdminTypes();
        getProjectBillingData();
        getProjectDetailsData();
        getWorkClassifications();
        getProjectAdministration(appContext.clientNumber, appContext.projectNumber);
        getLandmen(true);
        setUsers(undefined);
    }, []);

    useEffect(() => {
        getLandmen(false);
    }, [landmenFilters]);

    useEffect(() => {
        setSelectedLandmen([]);
    }, [projectStaff]);

    useEffect(() => {
        getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
        getLandmen(true);
        setChangesSubmitted(false);
    }, [staffComplete]);

    const getLandmen = async (staffed: boolean) => {
        setIsLoading(true);
        console.log('Current Filter', landmenFilters);
        let unselectedFilter = landmenFilters;
        if (unselectedFilter.length > 0) {
            unselectedFilter += '--and--where eq selected,' + staffed;
        } else {
            unselectedFilter = '?$filter=where eq selected,' + staffed;
        }
        let data = await get(`v1.0/landmen/client/${appContext.clientNumber}/project/${appContext.projectNumber}${unselectedFilter}`);

        if (response.ok) {
            if (staffed) {
                setStaffedLandmen(data.items);
                setIsLoading(false);
            } else {
                setLandmen(data.items);
                setIsLoading(false);
            }
        } else {
            console.log('bad request', response, unselectedFilter);
        }
    };

    const getProjectStaffing = async (clientId: number, projectId: number) => {
        setIsLoading(true);
        let data: PagedResultSet<ProjectStaff> = await get(`v1.0/clients/${clientId}/projects/${projectId}/projectstaff/staffing`);

        if (response.ok) {
            data?.items?.forEach((item, key) => {
                data[key] = item;
            });
            console.log('Project Staff data', data);
            setProjectStaff(data);
            setIsLoading(false);
        } else {
            console.log('Unable to retrieve Staffing Data.');
        }
    };

    const getProjectAdministration = async (clientId: number, projectId: number) => {
        let data = await get(`v1.0/clients/${clientId}/projects/${projectId}/projectstaff/administration`);

        if (response.ok) {
            setProjectAdminStaff(data);
        } else {
            console.log('Unable to retrieve Staffing Data.');
        }
    };

    const getProjAdminTypes = async () => {
        let data = await get(`v1.0/users/pm-types`);

        if (response.ok) {
            setProjectAdministrationTypes(data);
        } else {
            console.log('Unable to retrieve Project Admin Types.');
        }
    };

    const getPCAPMUsers = async () => {
        let data: PagedResultSet<User> = await get(`v1.0/users`);

        if (response.ok) {
            setPcapmList(data.items);
        } else {
            console.log('Unable to retrieve PCAPM List.');
        }
    };

    const getBillingCycles = async () => {
        let data = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/billing-cycles`);

        if (response.ok) {
            setBillingCycles(data);
        }
    };

    const getWorkClassifications = async () => {
        let data = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/work-classifications`);

        if (response.ok) {
            setWorkClassifications(data);
        }
    };

    const getProjectBillingData = async () => {
        const data: ProjectBilling = await get(`v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/project-billing`);

        if (response.ok) {
            setProjectBilling(data);
        } else {
            console.log('Unable to get Project Billing Data');
        }
    };

    const getProjectDetailsData = async () => {
        const data = await get(`v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/project-details`);

        if (response.ok) {
            setProjectDetails(data);
        } else {
            console.log('Unable to get Project Details data');
        }
    };

    const toggleSelectedEntities = (landmanId: number) => {
        let newEntities = [...selectedLandmen];

        if (newEntities.indexOf(newEntities.find((ne) => ne.id === landmanId)) > -1) {
            newEntities.splice(newEntities.indexOf(newEntities.find((ne) => ne.id === landmanId)), 1);
        } else {
            newEntities.push(landmen.find((lm) => lm.id === landmanId));
        }

        setSelectedLandmen(newEntities);
    };

    const displayLandmen = () => {
        if (landmen?.length > 0) {
            return landmen?.map((landman, key) => {
                let cardClass = selectedLandmen?.indexOf(selectedLandmen?.find((sl) => sl.id === landman.id)) > -1 ? ' selected' : '';
                let currentAddress = 'N/A';
                let currentPhone = 'N/A';
                if (!!landman.primaryAddress) {
                    let add = landman.primaryAddress;
                    currentAddress = `${add.addressLine1} ${add.addressLine2}, ${add.city}, ${add.state} ${add.zipCode}`;
                }
                if (!!landman.primaryPhoneNumber) {
                    let pho = landman.primaryPhoneNumber;
                    currentPhone = `${pho.number} Ext: ${pho?.extension?.length > 0 ? pho?.extension : 'N/A'}`;
                }

                return (
                    <Card key={key} className={`small-input${cardClass}`} newContext={false} onClick={() => toggleSelectedEntities(landman.id)}>
                        <div style={{ display: 'flex' }}>
                            <div className="mr-2" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ padding: '1rem', width: '30%' }}>
                                        <div>Name: {`${landman.firstName} ${landman.lastName}`}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '40%' }}>
                                        <div>Address: {currentAddress}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '30%' }}>
                                        <div>Phone: {currentPhone}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                );
            });
        } else {
            return <div>No Current Records</div>;
        }
    };

    const hideDialog = () => actions.layout.hideDialog();
    const getUsersByOptions = (options: ODataOptions) => actions.user.getUsersByOptions(options);
    const initUsers = () => actions.user.initUsers();
    const addUserToStaff = (clientId: number, projectId: number, staffInfo: ProjectAdministratorStaff) => {
        actions.user.addProjectAdministrationStaff(clientId, projectId, staffInfo);
    };

    const onSubmit = () => {
        setChangesSubmitted(true);
        // let staffedIds = projectStaff?.items?.map((ps) => {
        //     return ps.user?.id;
        // });

        // staffedIds.forEach((si) => {
        //     if (!selectedLandmen?.includes(selectedLandmen.find((sl) => sl.id === si))) {
        //         let staffToRemove: ProjectStaff = projectStaff?.items?.find((ps) => ps.user.id === si);
        //         let now = new Date().getTime();
        //         staffToRemove.endBillingCycle = billingCycles.find((bc) => {
        //             return new Date(bc.startDate).getTime() <= now && new Date(bc.endDate).getTime() + 86400000 > now;
        //         });
        //         addOrUpdateProjectStaffing(staffToRemove);
        //     }
        // });
    };

    const formatProjectStaffData = (data: ProjectStaff) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = appContext.projectNumber;
        data.userId = data.userId ? parseInt(data.userId + '', 10) : 0;
        data.projectAdministratorId = data.projectAdministratorId ? parseInt(data.projectAdministratorId + '', 10) : null;
        data.projectManagerId = data.projectManagerId ? parseInt(data.projectManagerId + '', 10) : null;
        data.pcapmId = data.pcapmId ? parseInt(data.pcapmId + '', 10) : null;
        data.workClassificationId = data.workClassificationId ? parseInt(data.workClassificationId + '', 10) : 0;
        data.startBillingCycleId = data.startBillingCycleId
            ? parseInt(data.startBillingCycleId + '', 10)
            : data.startBillingCycle
            ? parseInt(data.startBillingCycle.id + '', 10)
            : null;
        data.endBillingCycleId = data.endBillingCycleId
            ? parseInt(data.endBillingCycleId + '', 10)
            : data.endBillingCycle
            ? parseInt(data.endBillingCycle.id + '', 10)
            : null;
        data.startBillingCycle = billingCycles ? billingCycles.find((bc: BillingAndPaymentCycle) => bc.id === data.startBillingCycleId) : null;
        data.endBillingCycle = billingCycles ? billingCycles.find((bc: BillingAndPaymentCycle) => bc.id === data.endBillingCycleId) : null;
        data.overrideBillRate = data.overrideBillRate ? parseFloat(data.overrideBillRate + '') : 0;
        data.overridePayRate = data.overridePayRate ? parseFloat(data.overridePayRate + '') : 0;
        data.ownershipAccess = !!data.ownershipAccess ? data.ownershipAccess : null;
        data.reportAccess = !!data.reportAccess ? data.reportAccess : null;
        data.contractAccess = !!data.contractAccess ? data.contractAccess : null;
        data.startDate = data.startDate ? (moment(data.startDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.endDate = data.endDate ? (moment(data.endDate).format('YYYY-MM-DD') as unknown as Date) : null;

        return data;
    };

    const addOrUpdateProjectStaffing = async (data: ProjectStaff) => {
        let newData = formatProjectStaffData(data);

        console.log('after sanitization ', newData);

        if (!newData.id || newData.id === 0) {
            console.log('no id found, posting new staff', newData);
            await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/staffing`, newData);
            setStaffComplete(true);
        }

        getLandmen(false);
        setStaffComplete(false);
    };

    const updateFilter = (options: ODataOptions) => {
        const stringOptions = options.toString();
        searchUpdate(stringOptions);
    };

    const searchUpdate = useCallback(
        _.debounce((filter: string) => {
            setLandmenFilters(filter);
        }, 800),
        []
    );

    const editLandmenDialog: ContextMenuAction[] = [
        { text: 'Manage Landmen', action: () => dispatch(actions.layout.showDialog(DialogList.STAFFING_LANDMEN)) },
    ];

    const updateAfterDestaffed = (data: boolean) => {
        if (!!data) {
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
            getLandmen(false);
        }
    };

    const updateAfterNewData = (data: boolean) => {
        if (!!data) {
            getProjectStaffing(appContext.clientNumber, appContext.projectNumber);
        }
    };

    const landmanCard = () => {
        if (!isLoading && projectStaff?.items?.length > 0) {
            return projectStaff?.items?.map((ps, key) => {
                let landman: Landman = staffedLandmen.find((lm) => lm.id === ps.userId);
                if (!!landman) {
                    return (
                        <StaffedLandmanCard
                            projectStaffInfo={ps}
                            landman={landman}
                            index={key}
                            key={key}
                            pcapmList={pcapmList}
                            projectAdminStaff={projectAdminStaff?.items}
                            submitListener={changesSubmitted}
                            submitStaffingForm={addOrUpdateProjectStaffing}
                            billingCycles={billingCycles}
                            workClassifications={workClassifications}
                            showDialog={editLandmenDialog}
                            hideDialog={hideDialog}
                            deStaffedListener={updateAfterDestaffed}
                            updateCardListener={updateAfterNewData}
                        />
                    );
                }
                return <div key={key} />;
            });
        }
        return <LoadingSpinner />;
    };

    // JWF code ends here

    return (
        <>
            <RHFLegend title="Project Staffing" />
            <CardSection title="Project's Current Staff">
                <CardContainer>
                    {isLoading && !projectStaff?.items && <LoadingSpinner />}
                    {projectStaff?.items?.length === 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', height: '50px' }}>
                            <p>No employees have been staffed to this project!</p>
                        </div>
                    )}
                    {projectStaff?.items?.length > 0 && (
                        <div className="mb-1 mt-1">
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', margin: '0 5.5rem 0 3.5rem' }}>
                                <h3 style={{ marginRight: '10rem' }}>Staff</h3>
                                <h3 style={{ marginRight: '4rem' }}>Start Period</h3>
                                <h3 style={{ marginRight: '5rem' }}>End Period</h3>
                                <h3 style={{ marginRight: '5rem' }}>Pay Rate</h3>
                                <h3 style={{ marginRight: '8rem' }}>Bill Rate</h3>
                                <h3 style={{ marginRight: '3rem' }}>PC/APM</h3>
                                <h3 style={{ marginRight: '3rem' }}>Work Classification</h3>
                                <h3 style={{ marginRight: '8rem' }}>Project Manager</h3>
                                <h3>Landmen Permissions</h3>
                            </div>
                            <div style={{ overflowY: 'scroll', maxHeight: '90vh' }}>{!!projectStaff?.items && landmanCard()}</div>
                        </div>
                    )}
                </CardContainer>
            </CardSection>

            <CardSection title="">
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        {selectedLandmen?.length > 0 && (
                            <>
                                <RHFLegend title="Selected Staff" />
                                <CardContainer style={{ overflowY: 'scroll' }}>
                                    <div className="mb-1 mt-1">
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-around',
                                                margin: '0 5.5rem 0 3.5rem',
                                            }}
                                        >
                                            <h3 style={{ marginRight: '10rem' }}>Staff</h3>
                                            <h3 style={{ marginRight: '4rem' }}>Start Period</h3>
                                            <h3 style={{ marginRight: '5rem' }}>End Period</h3>
                                            <h3 style={{ marginRight: '5rem' }}>Pay Rate</h3>
                                            <h3 style={{ marginRight: '8rem' }}>Bill Rate</h3>
                                            <h3 style={{ marginRight: '3rem' }}>PC/APM</h3>
                                            <h3 style={{ marginRight: '3rem' }}>Work Classification</h3>
                                            <h3 style={{ marginRight: '8rem' }}>Project Manager</h3>
                                            <h3>Landmen Permissions</h3>
                                        </div>
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            {selectedLandmen?.map((sl, key) => {
                                                let user = pcapmList ? pcapmList.find((pc) => pc.id === sl.id) : null;

                                                return (
                                                    <StaffingLandmanCard
                                                        projectStaffInfo={projectStaff ? projectStaff?.items.find((ps) => ps.userId === sl.id) : {}}
                                                        landman={sl}
                                                        index={key}
                                                        key={key}
                                                        userList={pcapmList}
                                                        projectAdminStaff={projectAdminStaff?.items}
                                                        submitListener={changesSubmitted}
                                                        submitStaffingForm={addOrUpdateProjectStaffing}
                                                        billingCycles={billingCycles}
                                                        workClassifications={workClassifications}
                                                        showDialog={editLandmenDialog}
                                                        hideDialog={hideDialog}
                                                        defaultBillRate={projectBilling?.dayRate}
                                                        defaultPayRate={user?.defaultPayRate ? user?.defaultPayRate : 0}
                                                        defaultPM={projectDetails ? projectDetails.projectManagerId : 0}
                                                        defaultPCAPM={projectDetails ? projectDetails.pcapmId : 0}
                                                    />
                                                );
                                            })}
                                            <div style={{ padding: '1rem' }}>
                                                <Button text="Submit" intent="primary" type="submit" />
                                            </div>
                                        </form>
                                    </div>
                                </CardContainer>
                            </>
                        )}
                        <div className="filter-collapsible">
                            <div className="header" {...getToggleProps()}>
                                {isExpanded ? 'Hide' : 'Find Landmen'}
                            </div>
                            <div {...getCollapseProps()}>
                                <div className="collapsible-content">
                                    <Filter
                                        sortItems={[
                                            { group: 1, text: 'Name', icon: 'alpha-up', orderName: 'name', orderDirection: 'ascending' },
                                            { group: 1, text: 'Name Desc', icon: 'alpha-down', orderName: 'name', orderDirection: 'descending' },
                                            { group: 1, text: 'Number', icon: 'numeric-up', orderName: 'projectNumber', orderDirection: 'ascending' },
                                            {
                                                group: 1,
                                                text: 'Number Desc',
                                                icon: 'numeric-down',
                                                orderName: 'projectNumber',
                                                orderDirection: 'descending',
                                            },
                                        ]}
                                        filterItems={[
                                            { group: 1, text: 'First Name', orderName: 'firstName', orderDirection: 'descending' },
                                            { group: 1, text: 'Last Name', orderName: 'lastName', orderDirection: 'descending' },
                                            { group: 1, text: 'Username', orderName: 'username', orderDirection: 'descending' },
                                            { group: 1, text: 'City', orderName: 'city', orderDirection: 'descending', type: 'date' },
                                            { group: 1, text: 'State', orderName: 'state', orderDirection: 'descending', type: 'date' },
                                            { group: 1, text: 'LMS Number', orderName: 'lmsNumber', orderDirection: 'descending', type: 'number' },
                                        ]}
                                        onFilterChanged={updateFilter}
                                    />
                                    <CardContainer>{displayLandmen()}</CardContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardSection>
            <AddAdminStaffDialog
                clientNumber={appContext.clientNumber}
                projectNumber={appContext.projectNumber}
                itemType="Client"
                dialogName={DialogList.ADD_STAFF}
                getProjAdminTypes={getProjAdminTypes}
                projectAdministrationTypes={projectAdministrationTypes}
                getUsersByOptions={getUsersByOptions}
                onCloseDialog={initUsers}
                users={users}
                addUserToStaff={addUserToStaff}
                hideDialog={hideDialog}
            />
        </>
    );
};

export default StaffingPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/StaffingPage.tsx